import { BevelFilter } from "@pixi/filter-bevel";
import { GlowFilter } from "@pixi/filter-glow";
import { OutlineFilter } from "@pixi/filter-outline";
import { TextConfig } from "slotworks/config/asset-config-schema";
import { ButtonState } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import BitmapText from "slotworks/graphics/pixi/bitmap-text";
import { ExtendedTextStyle, MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import SettingsModel from "slotworks/model/settings/settings-model";
import { Services } from "slotworks/services/services";
import { FontSubstitute } from "slotworks/services/translations/translations";
import { contains } from "slotworks/utils/collection-utils";

class TextPopulator {

    private fontFallbacks: Map<string, string> = new Map();
    private defaultFallback: string;

    /**
     * List fallbacks for each font (lower case, no spaces), and/or a default which all fonts will fall back to as a last resort
     * @param fontFallbacks Map<string, string>
     * @param defaultFallback string
     */
    public setFontFallbacks(fontFallbacks: Map<string, string>, defaultFallback?: string) {
        this.fontFallbacks = fontFallbacks;
        this.defaultFallback = defaultFallback;
    }

    public populateText(texts: TextConfig[], layer: Layers) {
        // Populate text as designed in asset works
        if (texts) {
            for (const textConfig of texts) {

                // Text
                const id = textConfig.id;
                const idParts = id.split("-");

                let text: BitmapText | MultiStyleText;

                if (textConfig.fontVariant === "bmp") {
                    // Bitmap font
                    text = new BitmapText(textConfig.content,
                        {
                            font: {
                                size: textConfig.size,
                                name: textConfig.fontFamily
                            }
                        });
                    text.positionWidth = textConfig.width;
                    text.positionHeight = textConfig.height;

                } else {
                    // Substitute fonts
                    let fontFamily = textConfig.fontFamily + "-" + textConfig.fontVariant;

                    const fontSubstitutes = Services.translations.getFontSubstitutes();
                    if (fontSubstitutes.has(fontFamily)) {
                        const substitutes: FontSubstitute[] = fontSubstitutes.get(fontFamily);
                        substitutes.forEach((substitute: FontSubstitute, index: number) => {
                            const language = SettingsModel.language;
                            if (!substitute.targetInstances || (substitute.targetInstances && contains(substitute.targetInstances, id))) {
                                if (substitute.includedLanguages && contains(substitute.includedLanguages, language)) {
                                    fontFamily = substitute.font;
                                }
                                if (substitute.excludedLanguages && !contains(substitute.excludedLanguages, language)) {
                                    fontFamily = substitute.font;
                                }
                            }
                        });
                    }

                    // List fonts and fallbacks
                    const fonts = [fontFamily];
                    if (this.fontFallbacks.has(fontFamily)) {
                        fonts.push(this.fontFallbacks.get(fontFamily));
                    }
                    if (this.defaultFallback) {
                        fonts.push(this.defaultFallback);
                    }

                    // Regular text
                    const textFormat = {
                        fontFamily: fonts,
                        fontSize: textConfig.size,
                        fill: textConfig.color,
                        wordWrap: true,
                        wordWrapWidth: textConfig.width,
                        height: textConfig.height,
                        width: textConfig.width,
                        breakWords: Services.translations.breakWords,
                        trim: false,
                        align: textConfig.align
                    } as ExtendedTextStyle;

                    // Stroke
                    if (textConfig.strokeThickness) {
                        textFormat.stroke = textConfig.stroke;
                        textFormat.strokeThickness = textConfig.strokeThickness * 2;
                    }

                    // Gradient fill
                    if (textConfig.fill) {
                        textFormat.fill = textConfig.fill;
                        textFormat.fillGradientStops = textConfig.fillGradientStops;
                        textFormat.fillGradientType = textConfig.fillGradientType;
                    }

                    // Drop shadow
                    if (textConfig.dropShadowBlur) {
                        textFormat.dropShadow = true;
                        textFormat.dropShadowAlpha = textConfig.dropShadowAlpha;
                        textFormat.dropShadowAngle = (180 - textConfig.dropShadowAngle) * Math.PI / 180;
                        textFormat.dropShadowBlur = textConfig.dropShadowBlur / 2;
                        textFormat.dropShadowColor = textConfig.dropShadowColor;
                        textFormat.dropShadowDistance = 1 + textConfig.dropShadowDistance;
                    }

                    // Content and localisation
                    let content = textConfig.content;

                    if (content.slice(0, 1) === "[") {
                        const translationID = content.slice(1, content.indexOf("]"));
                        content = this.processTranslation(translationID);
                    }

                    if (content.indexOf("#") > -1) {
                        content = "";
                    }

                    const filters: Array<PIXI.Filter<any>> = [];

                    if (textConfig.innerShadow) {
                        const innerShadow = textConfig.innerShadow;
                        const color = this.colourStringToHex(innerShadow.color);
                        const bevel = new BevelFilter({
                            rotation: innerShadow.rotation,
                            thickness: innerShadow.thickness * 2,
                            lightColor: 0xfffff,
                            lightAlpha: 0,
                            shadowColor: color,
                            shadowAlpha: innerShadow.alpha
                        });
                        filters.push(bevel);
                    }

                    if (textConfig.outerGlow) {
                        const outerGlow = textConfig.outerGlow;
                        const glow = new GlowFilter(outerGlow.blur * 0.5, Math.max(1, outerGlow.spread), 0, this.colourStringToHex(outerGlow.color), 1);
                        filters.push(glow);
                    }

                    if (filters.length > 0 && textConfig.strokeThickness > 0) {
                        textFormat.stroke = 0;
                        textFormat.strokeThickness = 0;

                        const colour = this.colourStringToHex(textConfig.stroke);
                        const stroke = new OutlineFilter(textConfig.strokeThickness * 2, colour);

                        filters.push(stroke);
                    }

                    text = new MultiStyleText(content, {
                        default: textFormat,
                        ...Services.translations.formats
                    });

                    text.filters = filters;
                }

                text.name = id;

                text.move(textConfig.x, textConfig.y, textConfig.align);

                if (idParts[0] === "lbl") {
                    layer.add(text, false, false, text.name + "-" + idParts[2]);
                    this.processLabel(layer, text, idParts[1], idParts[2]);
                } else {
                    layer.add(text, false, false, text.name);
                }
            }
        }
    }

    private processTranslation(translationID: string): string {
        let translation = Services.translations.get(translationID);

        const pTagsToStrip = /(?:<p>)|(?:<\/p>(?!.|\s*<\/p>))/gim;
        const pTagsToReplace = /<\/p>/gi;

        translation = translation.replace(pTagsToStrip, "");
        translation = translation.replace(pTagsToReplace, "\n\n");

        return translation;
    }

    private processLabel(layer: Layers, label: BitmapText | MultiStyleText, buttonName: string, state: string) {
        const button = layer.getButton(buttonName);

        if (button) {
            button.addLabelForState(label, ButtonState.FromString(state));
        }
    }

    private colourStringToHex(color: string): number {
        return parseInt("0x" + color.substr(1));
    }

}

export default new TextPopulator();
